import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, aboutInfo, faqData } from '@/mocks/comics';

export default function About() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    if (formData.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const fd = new FormData(form);
      const params = new URLSearchParams();
      fd.forEach((value, key) => {
        if (key !== 'website_alt') params.append(key, value as string);
      });
      const res = await fetch('https://readdy.ai/api/form/d9ontbaftmtnfcfh0al0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || (parsed as { meta?: { detail?: string } })?.meta?.detail
          || responseText
          || t('about.contact.error');
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError(t('about.contact.error'));
      setFormStatus('error');
    }
  }, [formData.message, t]);

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-8 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: t('about.pageTitle') }]} />

            {/* Brand Intro */}
            <section className="mt-8">
              <div className="text-center mb-10">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                  {t('about.brand.title')}
                </h1>
                <p className="text-sm text-foreground-500">{t('about.brand.subtitle')}</p>
              </div>
              <div className="max-w-3xl mx-auto space-y-5">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('about.brand.paragraph1')}</p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('about.brand.paragraph2')}</p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('about.brand.paragraph3')}</p>
              </div>
              <div className="max-w-3xl mx-auto mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                {[
                  { num: '2019', label: '成立年份' },
                  { num: '5000+', label: '漫画作品' },
                  { num: '300万+', label: '月活用户' },
                  { num: '99.9%', label: '服务可用率' },
                ].map((stat) => (
                  <div key={stat.label} className="p-4 rounded-lg bg-background-100">
                    <div className="text-xl md:text-2xl font-heading font-bold text-primary-600">{stat.num}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation / Testimonials */}
            <section className="mt-16 md:mt-20">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {t('about.reputation.title')}
                </h2>
                <p className="text-sm text-foreground-500">{t('about.reputation.subtitle')}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {testimonials.map((item, idx) => (
                  <div key={idx} className="p-5 md:p-6 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{item.content}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-600">
                          {item.name.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-foreground-800">{item.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mt-16 md:mt-20">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  常见问题
                </h2>
                <div className="space-y-3">
                  {faqData.map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-lg overflow-hidden">
                      <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                        <h3 className="text-sm md:text-base font-medium text-foreground-800 pr-4">{faq.question}</h3>
                        <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                      </summary>
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="mt-16 md:mt-20">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    {t('about.contact.title')}
                  </h2>
                  <p className="text-sm text-foreground-500">{t('about.contact.subtitle')}</p>
                </div>
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                  <div className="flex-1 space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-primary-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800">{t('about.contact.email')}</h3>
                        <p className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contactEmail}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-wechat-line text-accent-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800">{t('about.contact.wechat')}</h3>
                        <p className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contactWechat}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                        <i className="ri-qq-line text-secondary-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800">{t('about.contact.qq')}</h3>
                        <p className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contactQQ}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                      {t('about.contact.formTitle')}
                    </h3>
                    <div id="about-contact-form">
                      <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                        <div>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder={t('about.contact.namePlaceholder')}
                            required
                            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                            placeholder={t('about.contact.emailPlaceholder')}
                            required
                            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                            placeholder={t('about.contact.messagePlaceholder')}
                            required
                            rows={4}
                            maxLength={500}
                            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          />
                          <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                        </div>
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="form-honeypot"
                        />
                        <button
                          type="submit"
                          disabled={formStatus === 'loading'}
                          className="w-full px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-all cursor-pointer whitespace-nowrap"
                        >
                          {formStatus === 'loading' ? (
                            <span className="flex items-center justify-center gap-2">
                              <i className="ri-loader-4-line animate-spin" /> 提交中...
                            </span>
                          ) : (
                            t('about.contact.submit')
                          )}
                        </button>
                        {formStatus === 'success' && (
                          <p className="text-sm text-green-600 text-center">{t('about.contact.success')}</p>
                        )}
                        {formStatus === 'error' && (
                          <p className="text-sm text-red-500 text-center">{formError || t('about.contact.error')}</p>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于哔咔动漫",
            description: "了解哔咔动漫的品牌故事、平台口碑与联系方式",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://bikadongman.com/" },
                { "@type": "ListItem", position: 2, name: "关于我们", item: "https://bikadongman.com/about" },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}